import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { HiArrowLeft, HiCode } from 'react-icons/hi';
import api from '../utils/api';
import AppLoadingState from './common/AppLoadingState';
import { AppPageHero, AppPageShell } from './common/ui';

const DialogFlowChat = () => {
  const { dialogFlowId } = useParams();
  const navigate = useNavigate();
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogFlow, setdialogFlow] = useState(null);
  const [startNodes, setStartNodes] = useState([]);
  const [selectedStartNode, setSelectedStartNode] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [showEmbedCode, setShowEmbedCode] = useState(false);

  // First fetch dialogFlow details
  useEffect(() => {
    const fetchdialogFlowDetails = async () => {
      try {
        setLoading(true);
        const response = await api.getdialogFlow(dialogFlowId);
        setdialogFlow(response);
        
        // Get all nodes
        const nodes = response.nodes || [];
        setStartNodes(nodes);
        
        console.log('Available nodes:', nodes);
      } catch (error) {
        toast.error('Failed to fetch dialogFlow details');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (dialogFlowId) {
      fetchdialogFlowDetails();
    }
  }, [dialogFlowId]);

  // Initialize chat only when a start node is selected
  useEffect(() => {
    if (selectedStartNode) {
      initializeChat(selectedStartNode);
    }
  }, [selectedStartNode]);

  const initializeChat = async (startNodeId) => {
    try {
      setLoading(true);
      console.log('Initializing chat with start node:', startNodeId);
      
      const response = await api.createdialogFlowChat({
        dialogFlow_id: dialogFlowId,
        start_node_id: startNodeId
      });

      console.log('Chat creation response:', response);

      if (response.data?.chat_id) {
        const chatId = response.data.chat_id;
        setChatId(chatId);
        // Send initial message to start the conversation
        await sendInitialMessage(chatId);
      } else {
        toast.error('Failed to get chat ID from response');
        console.error('Invalid response format:', response);
      }
    } catch (error) {
      toast.error('Failed to initialize chat');
      console.error('Chat initialization error:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const sendInitialMessage = async (chatId) => {
    try {
      const response = await api.senddialogFlowMessage(chatId, "Hello");

      console.log('Initial message response:', response);
      
      // Update messages with chat history from response
      if (response.data?.chat_history) {
        setMessages(response.data.chat_history.slice(1));
        // Update current node
        setCurrentNode({
          id: response.data.current_node_id,
          name: response.data.current_node_name
        });
      }
    } catch (error) {
      console.error('Error sending initial message:', error);
      toast.error('Failed to start conversation');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || !chatId) return;

    const userMessage = inputMessage;
    try {
      // Add user message immediately
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setIsTyping(true);
      setInputMessage('');

      const response = await api.senddialogFlowMessage(chatId, userMessage);

      if (response.data?.chat_history) {
        setMessages(response.data.chat_history.slice(1));
        
        // Update current node
        setCurrentNode({
          id: response.data.current_node_id,
          name: response.data.current_node_name
        });
        console.log('Current node:', response.data.current_node_name);
      } else {
        console.log('Full response:', response);
        toast.error('No response message received');
        // Revert to previous messages if response fails
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (error) {
      toast.error('Failed to send message');
      console.error(error);
      // Revert to previous messages if request fails
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsTyping(false);
    }
  };

  const handleBack = () => {
    navigate('/app/dialog-flows/chats');
  };

  const getEmbedCode = () => {
    const embedCode = `<!-- Add this in your <head> tag -->
    <link href="http://localhost:3000/embed-styles.css" rel="stylesheet">
    <script src="http://localhost:3000/embed.js"></script>
    
    <!-- Add this where you want the chat to appear -->
    <div id="dialog-chat" 
      data-dialogFlow-id="${dialogFlowId}" 
      data-start-node-id="${selectedStartNode}"
      style="width: 400px; height: 600px;"
    ></div>`;
    return embedCode;
  };

  const handleCopyEmbedCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    toast.success('Embed code copied to clipboard!');
  };

  return (
    <AppPageShell maxWidthClass="max-w-4xl">
      <AppPageHero
        compact
        eyebrow="Dialog flow"
        title={dialogFlow?.name || 'Chat'}
        description="Choose a start node, then talk through the graph. Export embed code when you are ready to ship."
        lead={
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-[var(--app-muted)] transition hover:bg-[var(--app-accent-soft)] hover:text-[var(--app-ink)]"
          >
            <HiArrowLeft className="h-5 w-5" />
            All flows
          </button>
        }
        actions={
          selectedStartNode ? (
            <button
              type="button"
              onClick={() => setShowEmbedCode(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] px-4 py-2.5 text-sm font-semibold text-[var(--app-ink)] shadow-sm transition hover:border-[var(--app-accent-ring)]"
            >
              <HiCode className="h-5 w-5" />
              Export embed
            </button>
          ) : null
        }
      />

    <div className="relative flex flex-col h-[calc(100vh-14rem)] min-h-[24rem] rounded-xl border border-slate-200/80 dark:border-slate-600/50 bg-white/60 dark:bg-slate-900/35 backdrop-blur-sm overflow-hidden">

      {/* Embed Code Modal */}
      {showEmbedCode && (
        <div className="fixed inset-0 bg-black/55 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600/60 rounded-xl p-6 max-w-2xl w-full shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">Embed Code</h3>
            <div className="bg-slate-50 dark:bg-slate-950/70 border border-slate-200/80 dark:border-slate-600/50 p-4 rounded-lg mb-4">
              <pre className="whitespace-pre-wrap break-all text-sm text-slate-800 dark:text-slate-200">
                {getEmbedCode()}
              </pre>
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setShowEmbedCode(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white rounded-lg"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleCopyEmbedCode}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 dark:bg-teal-600 dark:hover:bg-teal-500"
              >
                Copy Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current node display */}
      {messages.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200/80 dark:border-slate-600/50 text-sm text-slate-600 dark:text-slate-300">
          Current Node: {currentNode?.name || 'Unknown'}
        </div>
      )}

      {!selectedStartNode && startNodes.length > 0 && (
        <div className="p-4 border-b border-slate-200/80 dark:border-slate-600/50 bg-white/50 dark:bg-slate-900/30">
          <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100">Select a node to start from:</h3>
          <div className="flex gap-2 flex-wrap">
            {startNodes.map((node) => (
              <button
                type="button"
                key={node.id}
                onClick={() => setSelectedStartNode(node.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  node.data?.isStart
                    ? 'bg-teal-50 dark:bg-teal-950/40 border-teal-200/80 dark:border-teal-500/30 text-teal-800 dark:text-teal-100 hover:bg-teal-100/80 dark:hover:bg-teal-900/45'
                    : 'bg-slate-100 dark:bg-slate-800/70 border-slate-200/90 dark:border-slate-600/50 text-slate-800 dark:text-slate-100 hover:bg-slate-200/80 dark:hover:bg-slate-700/80'
                }`}
              >
                {node.data?.name || 'Unnamed Node'}
                {node.data?.isStart && ' (Start)'}
                {node.data?.type && ` - ${node.data.type}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <AppLoadingState overlay showLabel={false} />}

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/40 dark:bg-slate-950/20">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                msg.role === 'user'
                  ? 'bg-teal-600 text-white dark:bg-teal-600'
                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/90 dark:border-slate-600/50 shadow-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-600/50 rounded-lg p-3 flex items-center space-x-1">
              <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-slate-200/80 dark:border-slate-600/50 bg-white/80 dark:bg-slate-900/50">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type your message..."
            className="flex-1 p-2.5 rounded-lg border border-slate-300 dark:border-slate-600/60 bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-600/25 dark:focus:ring-teal-400/20"
            disabled={isTyping || !chatId}
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={isTyping || !chatId}
            className="px-4 py-2 rounded-lg font-semibold text-white bg-teal-600 hover:bg-teal-500 dark:bg-teal-600 dark:hover:bg-teal-500 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </div>
    </AppPageShell>
  );
};

export default DialogFlowChat; 